import { useEffect, useState, useRef } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Lenis from "lenis";
import { Link } from "react-router-dom";

interface Project {
  title: string;
  description: string;
  features: string[];
  images: string[];
  reverse?: boolean;
  galleryTab: string;
}

// 1a4fa3
// 2674ef

const projects: Project[] = [
  {
    title: "Nakshatra Nagar",
    description:
      "Discover Nakshatra Nagar, a serene residential enclave nestled in the heart of Jabalpur, Madhya Pradesh. Embraced by lush greenery and scenic landscapes, Nakshatra Nagar offers a harmonious blend of modern living and natural beauty.",
    features: [
      "Tranquil Environment",
      "Strategic Location",
      "Modern Infrastructure",
      "Green Spaces",
      "Jain Mandir - Under Construction",
    ],
    images: [
      "\\assets\\imgs\\Projects\\Phase 1\\phase1a.webp",
      "\\assets\\imgs\\Projects\\Phase 1\\phase1.webp",
      "\\assets\\imgs\\Projects\\Phase 1\\IMG_7708.webp",
      "\\assets\\imgs\\Projects\\Phase 1\\phase1b.webp",
    ],
    galleryTab: "Nakshatra Nagar",
  },
  {
    title: "Nakshatra Nagar Phase 2 - Patan Road",
    description:
      "Introducing Our Newest Endeavor: Join us as we embark on our latest project, a testament to innovation, quality, and the Century Group commitment to excellence.",
    features: [
      "Tranquil Environment",
      "Strategic Location",
      "Modern Infrastructure",
      "Green Spaces",
      "Jain Mandir - Under Construction",
    ],
    images: [
      "\\assets\\imgs\\Projects\\Phase 2\\phase2.webp",
      "\\assets\\imgs\\Projects\\Phase 2\\0036.webp",
      "\\assets\\imgs\\Projects\\Phase 2\\2.webp",
      "\\assets\\imgs\\Projects\\Phase 2\\4.webp",
    ],
    reverse: true,
    galleryTab: "Nakshatra Nagar",
  },
  {
    title: "Nakshatra Nagar Phase 3 - Near Jain Mandir",
    description:
      "Discover Nakshatra Nagar, a serene residential enclave nestled in the heart of Jabalpur, Madhya Pradesh. Embraced by lush greenery and scenic landscapes.",
    features: [
      "Tranquil Environment",
      "Strategic Location",
      "Modern Infrastructure",
      "Green Spaces",
      "Jain Mandir - Under Construction",
    ],
    images: [
      "\\assets\\imgs\\Projects\\Phase 3\\0040.webp",
      "\\assets\\imgs\\Projects\\Phase 3\\070.webp",
      "\\assets\\imgs\\Projects\\Phase 3\\IMG_7708.webp",
      "\\assets\\imgs\\Projects\\Phase 3\\IMG_7757.webp",
    ],
    galleryTab: "Nakshatra Nagar",
  },
  {
    title: "Nakshatra Nagar - Phase 4",
    description:
      "Discover Nakshatra Nagar, a serene residential enclave nestled in the heart of Jabalpur, Madhya Pradesh.",
    features: [
      "Tranquil Environment",
      "Strategic Location",
      "Modern Infrastructure",
      "Green Spaces",
      "Jain Mandir - Under Construction",
    ],
    images: [
      "\\assets\\imgs\\Ongoing Projects\\2.webp",
      "\\assets\\imgs\\Ongoing Projects\\2.webp",
      "\\assets\\imgs\\Ongoing Projects\\2.webp",
      "\\assets\\imgs\\Ongoing Projects\\2.webp",
    ],
    reverse: true,
    galleryTab: "Nakshatra Nagar",
  },
  {
    title: "Star City - Phase 1,2,3",
    description:
      "Experience the joy of community living at Star Park. Engage with neighbors at community events, relax in parks and recreational areas.",
    features: [
      "Dream Project for many.",
      "Filled with greenery.",
      "1000+ residents living.",
    ],
    images: [
      "\\assets\\imgs\\Projects\\StarCity\\starcity1.webp",
      "\\assets\\imgs\\Projects\\StarCity\\starcity2.webp",
      "\\assets\\imgs\\Projects\\StarCity\\starcity3.webp",
      "\\assets\\imgs\\Projects\\StarCity\\starcity4.webp",
    ],
    galleryTab: "Star City",
  },
  {
    title: "Star Park - Shatabdipuram",
    description:
      "Experience the joy of community living at Star Park. Engage with neighbors at community events.",
    features: [
      "Prime Location - Vijay Nagar",
      "Safety and Security",
      "Premium Villas",
      "Modern Amenities",
    ],
    images: [
      "\\assets\\imgs\\Key Business\\a.webp",
      "\\assets\\imgs\\Key Business\\b.webp",
      "\\assets\\imgs\\Key Business\\c.webp",
      "\\assets\\imgs\\Key Business\\d.webp",
    ],
    reverse: true,
    galleryTab: "Star Park",
  },
];

const carouselSettings = {
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  fade: true,
  autoplay: true,
  autoplaySpeed: 3000,
};

const Projects: React.FC = () => {
  const [visibleStates, setVisibleStates] = useState<boolean[]>(
    new Array(projects.length).fill(false),
  );

  const projectRefs = useRef<(HTMLDivElement | null)[]>(
    Array(projects.length).fill(null),
  );

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(1 - t, 4)),
      smoothWheel: true,
    });

    const handleScroll = () => {
      const newVisibleStates = projects.map((_, index) => {
        const ref = projectRefs.current[index];
        if (ref) {
          const rect = ref.getBoundingClientRect();
          return rect.top < window.innerHeight && rect.bottom >= 0;
        }
        return false;
      });
      setVisibleStates(newVisibleStates);
    };

    lenis.on("scroll", handleScroll);
    handleScroll(); // Initial check

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => {
      lenis.off("scroll", handleScroll);
      lenis.destroy();
    };
  }, []);

  return (
    <div
      className="min-h-screen overflow-hidden"
      style={{ fontFamily: "Gambetta" }}
    >
      {/* Hero Section */}
      <section
        className="relative flex min-h-[75vh] w-full items-center justify-center"
        style={{
          backgroundImage: "url('/assets/imgs/ProjectsBG.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 text-center text-white">
          <h1
            className="animate-fade-in text-4xl font-bold tracking-widest md:text-5xl"
            style={{ fontFamily: "HistoryPro" }}
          >
            Our Projects
          </h1>
          {/* <p className="animate-fade-in-delay mx-auto mt-4 max-w-2xl text-lg md:text-xl">
            Explore our diverse portfolio of residential and community projects
            in Jabalpur, designed to blend modern living with natural serenity.
          </p> */}
        </div>
      </section>

      {/* Projects Section */}
      <main className="px-4 py-16 md:px-8">
        <div className="mx-auto max-w-6xl space-y-16">
          {projects.map((project, index) => (
            <section
              key={index}
              ref={(el: HTMLDivElement | null) => {
                projectRefs.current[index] = el;
              }}
              className={`flex flex-col gap-8 ${
                project.reverse ? "md:flex-row-reverse" : "md:flex-row"
              }`}
            >
              {/* Image Gallery */}
              <div
                className={`transition-all duration-1000 ease-in-out md:w-1/2 ${
                  visibleStates[index]
                    ? "translate-x-0 opacity-100"
                    : project.reverse
                      ? "translate-x-[100%] opacity-0"
                      : "translate-x-[-100%] opacity-0"
                }`}
              >
                <Slider {...carouselSettings}>
                  {project.images.map((image, imgIndex) => (
                    <div key={imgIndex} className="h-full w-full rounded-lg">
                      <img
                        src={image}
                        alt={`${project.title} Image ${imgIndex + 1}`}
                        className="h-96 w-full rounded-lg object-cover shadow-md transition-transform duration-300 hover:scale-110"
                      />
                    </div>
                  ))}
                </Slider>
              </div>

              {/* Project Details */}
              <div
                className={`flex flex-col justify-center transition-all duration-1000 ease-in-out md:w-1/2 ${
                  visibleStates[index]
                    ? "translate-y-0 opacity-100"
                    : "translate-y-10 opacity-0"
                }`}
              >
                <h1 className="mb-4 text-2xl font-bold text-gray-800 md:text-3xl">
                  {project.title}
                </h1>
                <p className="mb-6 text-gray-600">{project.description}</p>
                <ul className="list-disc pl-5 text-gray-700">
                  {project.features.map((feature, fIndex) => (
                    <li key={fIndex} className="mb-2">
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/gallery?tab=${encodeURIComponent(project.galleryTab)}`}
                  className="mt-6 flex items-center justify-between rounded-lg bg-[#1a4fa3] px-6 py-3 text-white transition-colors hover:bg-[#2674ef]"
                >
                  View Gallery
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>
            </section>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Projects;
